import { useFormatters } from '@/app/hooks/useFormatters'
import { ROUTES } from '@/app/lib/constants'
import { ICost, ICourse, IInstructor } from '@/types/course'
import { IEducationProvider } from '@/types/education-provider'
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { Chip, Typography } from '@mui/material'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import React, { useMemo } from 'react'

export type NewCourseCardCourseData = Pick<
  ICourse,
  'name' | 'learningDescription' | '_id' | 'cohorts' | 'level'
> & {
  educationProvider: Pick<IEducationProvider, '_id' | 'name' | 'logoUrl'>
  instructors: Pick<IInstructor, 'name' | 'avatarUrl' | 'role'>[]
  cost?: ICost
}

export type NewCourseCardProps = React.ComponentProps<'article'> & {
  course: NewCourseCardCourseData
  variant?: 'compact' | 'full'
  prefix?: React.ReactNode
}

export function NewCourseCard({
  course,
  prefix,
  variant = 'full',
  ...props
}: NewCourseCardProps) {
  const t = useTranslations('NewCourseCard')
  const {
    _id,
    name,
    learningDescription,
    level,
    educationProvider,
    instructors,
  } = course

  const { formatCourseDuration, formatCoursePrice } = useFormatters()
  const courseDuration = formatCourseDuration(course)
  const courseCost = formatCoursePrice(course)

  const LinkToCourse = ({ children, ...props }) => (
    <Link href={ROUTES.course.replace(':id', _id)} {...props}>
      {children}
    </Link>
  )

  const { logoUrl, name: educationProviderName = '' } = educationProvider

  const validInstructors = (instructors ?? []).filter(
    (instructor) => instructor && instructor.name && instructor.avatarUrl
  )

  const [instructor] = validInstructors
  const images = useMemo(
    () => [instructor?.avatarUrl, logoUrl].filter(Boolean),
    [instructor, logoUrl]
  )

  const instructorByLine = [instructor?.name, instructor?.role]
    .filter(Boolean)
    .join(', ')

  return (
    <article
      {...props}
      className={`NewCourseCard group rounded-lg border border-lines bg-mydra-white text-mydra-black hover:border-mydra-black ${props.className ?? ''}`}
      data-variant={variant}
    >
      <LinkToCourse>
        <div className="flex h-full flex-col gap-6 p-4">
          <header className="flex flex-col gap-4">
            <Typography
              variant="h4"
              className="inline-flex items-center gap-2 font-title text-[1.375rem] text-black"
            >
              {prefix && variant === 'compact' && (
                <span className="text-3xl font-bold text-mydra-mouse">
                  {prefix}
                </span>
              )}
              <span className="line-clamp-2 text-ellipsis">{name}</span>
            </Typography>
            {variant === 'full' && (
              <div className="flex items-center gap-2">
                {courseDuration && (
                  <Chip
                    className="!bg-mydra-medium-gray/40"
                    size="small"
                    icon={<AccessAlarmIcon className="text-inherit" />}
                    label={
                      courseDuration.at(0)?.toUpperCase() +
                      courseDuration.slice(1)
                    }
                  ></Chip>
                )}
                {level && (
                  <Chip
                    className={cn(
                      level === 'beginner'
                        ? 'bg-success-light text-success-dark'
                        : level === 'intermediate'
                          ? 'bg-warning-light/50 text-warning'
                          : level === 'advanced'
                            ? 'bg-mydra-purple-light text-mydra-purple'
                            : `!bg-mydra-medium-gray/40`
                    )}
                    size="small"
                    icon={<LayersOutlinedIcon className="text-inherit" />}
                    label={level?.charAt(0)?.toUpperCase() + level?.slice(1)}
                  ></Chip>
                )}
              </div>
            )}
          </header>
          <div className="mt-auto flex flex-col gap-4">
            <div
              className={`relative flex items-center gap-3 ${props.className ?? ''}`}
            >
              <div className="flex shrink-0 empty:hidden">
                {images.map((imageUrl, index) => (
                  <Image
                    key={`instructor-image-${index}-${course?._id ?? ''}`}
                    className="inline-block h-10 w-10 shrink-0 rounded-full border border-divider-light object-cover [&:not(:first-child)]:-ml-2"
                    src={imageUrl}
                    height={40}
                    width={40}
                    alt={t('instructor_image')}
                  />
                ))}
              </div>
              <div className="flex">
                <p
                  className="leading-[140%] text-mydra-black"
                  title={instructorByLine}
                >
                  <span className="line-clamp-1 capitalize">
                    {instructorByLine}
                  </span>
                  <span className="text-[--text]">{educationProviderName}</span>
                </p>
              </div>
            </div>
            {variant === 'full' && learningDescription && (
              <p className="line-clamp-2 text-ellipsis text-mydra-black">
                {learningDescription}
              </p>
            )}
          </div>
          {variant === 'full' && (
            <footer className="mt-auto flex items-center justify-between gap-6">
              {courseCost && (
                <p className="flex-1 text-xl font-semibold leading-7 text-mydra-black">
                  {courseCost}
                </p>
              )}
              <div className="mt-auto rounded-xl border border-lines px-4 py-3 text-center text-base leading-6 text-mydra-black group-hover:!bg-mydra-gray">
                {t('learn_more_and_enroll')}
              </div>
            </footer>
          )}
        </div>
      </LinkToCourse>
    </article>
  )
}
